import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./header.scss";
import { useSidenavStore } from "../../store/store";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

// components
import Notification from "./notification";
import EditProfile from "../Profile/EditProfile";
import ResetPassword from "../Profile/ResetPassword";

// images and icons
import maindashbaorIcom from "../../assets/images/maindashbaor-icon.svg";
import settingsBtIcon from "../../assets/images/settings-bt-icon.svg";
import images from "../../assets/images";
import { useAuth } from "../../auth/AuthProvider";

const Header: React.FC = () => {
  const notificationRef = useRef<HTMLDivElement>(null);
  const notiIconRef = useRef<HTMLAnchorElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountIconRef = useRef<HTMLLIElement>(null);
  const { isNav, setNav } = useSidenavStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, orgUser, orgAdmin, userType } = useAuth(); // Access user and logout from useAuth

  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [accountPop, setAccountPop] = useState<boolean>(false);
  const [isDashboard, setIsDashboard] = useState<boolean>(false);
  const [headerTitle, setHeaderTitle] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false); // Add loading state for logout
  const [showEditProfile, setShowEditProfile] = useState<boolean>(false);
  const [showResetPassword, setShowResetPassword] = useState<boolean>(false);
  const [userDisplayName, setUserDisplayName] = useState<string>("");

  const pathToTitle: { [key: string]: string } = {
    "/dashboard": "Dashboard",
    "/fire-system": "Fire Systems",
    "/fire-system/dashboard": "Fire Systems",
    "/premises": "Settings",
    "/regions": "Settings",
    "/departments": "Settings",
    "/policy-tags": "Settings",
    "/legal-contract-types": "Settings",
    "/suppliers": "Settings",
    "/vendor-list": "Settings",
    "/equipment": "Settings",
    "/manufacturers-list": "Settings",
    "/equipment-category": "Settings",
    "/equipment-subcategory": "Settings",
    "/location-master": "Settings",
    "/maintenancetype-list": "Settings",
    "/firerisk-processes": "Settings",
    "/firerisk-sub-processes": "Settings",
    "/budget-categories": "Settings",
    "/washroom-checklist-category": "Settings",
    "/washroom-types-list": "Settings",
    "/role-management/roles": "Settings",
    "/user-management/roles": "Settings",
    "/asset-equipment-category": "Settings",
    "/asset-equipment-subcategory": "Settings",
    "/emergency-category": "Settings",
    "/emergency-subcategory": "Settings",
    "/unit-of-measure-list": "Settings",
    "/incident-severity-levels": "Settings",
    "/hazard-material-category": "Settings",
    "/disposal-methods-list": "Settings",
    "/compliance-types": "Settings",
    "/utility-asset-type-list": "Settings",
    "/round-types": "Settings",
    "/master-data/equipment-management": "Settings",
    "/premises-information-view/:premiseId": "Laws & Regulations",

    // Fire Systems
    "/tasks": "Fire Systems",
    "/policies-manuals": "Fire Systems",
    "/folder/:id": "Fire Systems",
    "/risk-assessment": "Fire Systems",
    "/budgeting": "Fire Systems",
    "/trainings": "Fire Systems",
    "/trainings/create-planing": "Fire Systems",
    "/trainings/update-planing/:id": "Fire Systems",
    "/trainings/view/:id": "Fire Systems",
    "/trainings/evaluation": "Fire Systems",
    "/trainings/training-dashboard": "Fire Systems",
    "/trainings/create-test": "Fire Systems",
    "/trainings/start-test/:id": "Fire Systems",
    "/trainings/attempt-test/:id": "Fire Systems",
    "/trainings/training-view/:id": "Fire Systems",
    "/trainings/upcoming-sessions": "Fire Systems",
    "/trainings/upcoming-session-details/:id": "Fire Systems",
    "/trainings/past-evaluations": "Fire Systems",
    "/trainings/past-evaluation-details/:id": "Fire Systems",
    "/trainings/user-analytics": "Fire Systems",
    "/fire-drill": "Fire Systems",
    "/safety-equipment": "Fire Systems",
    "/safety-equipment/:equipmentTypeId": "Fire Systems",
    "/safety-equipment/inspection/:assetId?": "Fire Systems",
    "/safety-equipment/checklist-management": "Fire Systems",
    "/safety-equipment/fire-safety-scorecard": "Fire Systems",
    "/safety-equipment/fire-safety-scorecard/:id": "Fire Systems",
    "/safety-equipment/kpi-scorecard": "Fire Systems",
    "/safety-equipment/kpi-scorecard/:id": "Fire Systems",
    "/safety-equipment/fire-safety-mis-checklist": "Fire Systems",
    "/safety-equipment/fire-safety-mis-checklist/:id": "Fire Systems",
    "/safety-equipment/mis-checklist": "Fire Systems",
    "/safety-equipment/mis-checklist/:id": "Fire Systems",
    "/safety-equipment/kpi-checklist": "Fire Systems",
    "/safety-equipment/kpi-checklist-detail/:id": "Fire Systems",
    "/safety-equipment/electrical-safety-scorecard": "Fire Systems",
    "/safety-equipment/electrical-safety-scorecard/:id": "Fire Systems",
    "/fire-safety-incident": "Fire Systems",
    "/fires-safety-audits": "Fire Systems",
    "/perform-audits": "Fire Systems",

    // Incident Management
    "/fire-safety-incident-admin": "Incident Management",
    "/incident-dashboard": "Incident Management",
    "/incidents": "Incident Management",
    "/report": "Incident Management",

    // Laws & Regulations
    "/laws-regulations": "Laws & Regulations",
    "/laws-regulations-tasks": "Laws & Regulations",
    "/laws-regulations-dashboard": "Laws & Regulations",
    "/staff-management": "Laws & Regulations",
    "/facility-information": "Laws & Regulations",
    "/laws-regulations-audit": "Laws & Regulations",
    "/laws-regulations-policies": "Laws & Regulations",
    "/law-folder/:id": "Laws & Regulations",

    // Washroom And Area Maintenance
    "/washroom-management/dashboard": "Washroom And Area Maintenance",
    "/washroom-management/washrooms": "Washroom And Area Maintenance",
    "/washroom-management/washrooms/add-washroom":
      "Washroom And Area Maintenance",
    "/washroom-management/washrooms/view-washroom":
      "Washroom And Area Maintenance",
    "/washroom-management/washrooms/update-washroom":
      "Washroom And Area Maintenance",
    "/washroom-management/washrooms/update-status/:id":
      "Washroom And Area Maintenance",
    "/washroom-management/checklists": "Washroom And Area Maintenance",
    "/washroom-management/checklists/create": "Washroom And Area Maintenance",
    "/washroom-management/checklists/edit/:id": "Washroom And Area Maintenance",
    "/washroom-management/checklists/view/:id": "Washroom And Area Maintenance",
    "/washroom-management/reports": "Washroom And Area Maintenance",
    "/washroom-management/audits": "Washroom And Area Maintenance",
    "/washroom-management/audits/view/:auditId":
      "Washroom And Area Maintenance",
    "/washroom-management/audits/history": "Washroom And Area Maintenance",
    "/washroom-management/issues-reports": "Washroom And Area Maintenance",

    // Equipment Management
    "/equipment-management": "Equipment Management",
    "/equipment-management/dashboard": "Equipment Management",
    "/equipment-management/equipmentTasks": "Equipment Management",
    "/equipment-management/equipment-inventory-registration":
      "Equipment Management",
    "/equipment-management/documentation-and-history": "Equipment Management",
    "/equipment-management/maintenance-scheduling-and-tracking":
      "Equipment Management",
    "/equipment-management/maintenance-scheduling-and-tracking/check-list":
      "Equipment Management",
    "/equipment-management/maintenance-scheduling-and-tracking/create-check-list":
      "Equipment Management",
    "/equipment-management/maintenance-scheduling-and-tracking/view-check-list":
      "Equipment Management",
    "/equipment-management/report-and-analytics": "Equipment Management",
    "/equipment-management/incident": "Equipment Management",
    "/equipment-management/budgeting": "Equipment Management",
    // Equipment Management - Staff Training & Evaluation
    "/equipment-management/staff-training-evaluation/dashboard":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/upcoming-sessions":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/upcoming-sessions/:id":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/past-evaluations":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/past-evaluations/:id":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/trainings/create":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/trainings/:id/edit":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/trainings/:id/view":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/training-view/:id":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/start-test/:id":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/create-test":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/attempt-test/:id":
      "Equipment Management",
    "/equipment-management/staff-training-evaluation/user-analytics":
      "Equipment Management",
    "/equipment-management/rfid-demo-console": "Equipment Management",

    // Hazardous Materials
    "/hazardous-materials/inventory": "Hazardous Materials",
    "/hazardous-materials/safety-compliance-info": "Hazardous Materials",
    "/hazardous-materials/storage-records": "Hazardous Materials",
    "/hazardous-materials/tracking-logs": "Hazardous Materials",
    "/hazardous-materials/spill-reporting": "Hazardous Materials",
    "/hazardous-materials/inspection-checks": "Hazardous Materials",
    "/hazardous-materials/disposal-management": "Hazardous Materials",
    "/hazardous-materials/waste-tracking": "Hazardous Materials",
    "/hazardous-materials/search-reporting": "Hazardous Materials",
    "/hazardous-materials/checklist-categories": "Hazardous Materials",
    "/hazardous-materials/checklists": "Hazardous Materials",
    "/hazardous-materials/policies-manuals": "Hazardous Materials",
    "/hazardous-materials/policies-manuals/folder/:id": "Hazardous Materials",
    "/hazardous-materials/testing-drills": "Hazardous Materials",
    "/hazardous-materials/risk-assessment": "Hazardous Materials",
    "/hazardous-materials/audits/perform-audits": "Hazardous Materials",
    // Hazardous Materials - Staff Training & Evaluation
    "/hazardous-materials/staff-training-evaluation/dashboard":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/upcoming-sessions":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/upcoming-sessions/:id":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/past-evaluations":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/past-evaluations/:id":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/trainings/create":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/trainings/:id/edit":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/trainings/:id/view":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/training-view/:id":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/start-test/:id":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/create-test":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/attempt-test/:id":
      "Hazardous Materials",
    "/hazardous-materials/staff-training-evaluation/user-analytics":
      "Hazardous Materials",

    // External Emergency Preparedness
    "/external-emergency-preparedness/dashboard":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/emergency-response-plans":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/add-response-plan":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/bcp-plans":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/add-bcp-plan":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/bcp-plans/:planId/recovery-tasks":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/bcp-plans/:planId/add-recovery-task":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/bcp-plans/:planId/recovery-tasks/:taskId":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/bcp-plans/:planId/recovery-timeline":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/testing-drills":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/risk-assessment":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/external-agencies-coordination":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/resource-for-readiness":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/hazard-and-vulnerability-assessment":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/notifications":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/send-notification":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/notifications/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/erp-executions":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/trigger-erp":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/erp-executions/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/monitoring":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/ics": "External Emergency Preparedness",
    "/external-emergency-preparedness/incidents":
      "External Emergency Preparedness",
    // External Emergency Preparedness - Staff Training & Evaluation
    "/external-emergency-preparedness/staff-training-evaluation/dashboard":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/upcoming-sessions":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/upcoming-sessions/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/past-evaluations":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/past-evaluations/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/trainings/create":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/trainings/:id/edit":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/trainings/:id/view":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/training-view/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/start-test/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/create-test":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/attempt-test/:id":
      "External Emergency Preparedness",
    "/external-emergency-preparedness/staff-training-evaluation/user-analytics":
      "External Emergency Preparedness",

    // Utility Systems
    "/utility-systems/dashboard": "Utility Systems",
    "/utility-systems/asset-inventory-tracking": "Utility Systems",
    "/utility-systems/maintenance/schedules": "Utility Systems",
    "/utility-systems/maintenance/logs": "Utility Systems",
    "/utility-systems/project-planning": "Utility Systems",
    "/utility-systems/infection-control": "Utility Systems",
    "/utility-systems/compliance-safety": "Utility Systems",
    "/utility-systems/space-management": "Utility Systems",
    "/utility-systems/contractor-vendor-management": "Utility Systems",
    "/utility-systems/design-documentation-integration": "Utility Systems",
    "/utility-systems/design-documentation-integration/folder/:id":
      "Utility Systems",
    "/utility-systems/iot/dashboard": "Utility Systems",

    // Safety & Facility Rounds
    "/safety-facility-rounds/dashboard": "Safety & Facility Rounds",
    "/safety-facility-rounds/round-planning": "Safety & Facility Rounds",
    "/safety-facility-rounds/checklists": "Safety & Facility Rounds",
    "/safety-facility-rounds/checklists/create": "Safety & Facility Rounds",
    "/safety-facility-rounds/checklists/edit/:id": "Safety & Facility Rounds",
    "/safety-facility-rounds/checklists/view/:id": "Safety & Facility Rounds",
    "/safety-facility-rounds/issues": "Safety and Facility Rounds",
    "/safety-facility-rounds/issues/create": "Safety and Facility Rounds",
    "/safety-facility-rounds/issues/:id": "Safety and Facility Rounds",
    "/safety-facility-rounds/issues/edit/:id": "Safety and Facility Rounds",
    "/safety-facility-rounds/issues/view/:id": "Safety and Facility Rounds",
    "/safety-facility-rounds/notifications": "Safety and Facility Rounds",
    "/safety-facility-rounds/escalation-rules": "Safety and Facility Rounds",

    // Security
    "/security/dashboard": "Security",
    "/security/access-control": "Security",
    "/security/access-control/zones": "Security",
    "/security/access-control/assignments": "Security",
    "/security/access-control/logs": "Security",
    "/security/visitor-management": "Security",
    "/security/surveillance": "Security",
    "/security/surveillance/cameras": "Security",
    "/security/surveillance/live": "Security",
    "/security/surveillance/health": "Security",
    "/security/surveillance/motion-alerts": "Security",
    "/security/surveillance/heatmap": "Security",
    "/security/surveillance/vms": "Security",
    "/security/surveillance/incident-clips": "Security",
    "/security/incidents": "Security",
    "/security/command-center": "Security",
    "/security/parking": "Security",
    "/security/compliance-audit-reporting": "Security",
    "/security/compliance-audit": "Security",
    "/security/reports": "Security",
    "/security/asset-security": "Security",
    "/security/staff-safety": "Security",
    "/security/cyber-physical": "Security",
    "/security/ai-predictive": "Security",
    "/security/integrations": "Security",
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout(); // Use the logout function from useAuth
      toast.success("Logged out successfully!");
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Failed to logout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClickOutsideNoti = (event: MouseEvent) => {
    if (
      notificationRef.current &&
      !notificationRef.current.contains(event.target as Node) &&
      notiIconRef.current &&
      !notiIconRef.current.contains(event.target as Node)
    ) {
      setShowNotification(false);
    }
    if (
      accountRef.current &&
      !accountRef.current.contains(event.target as Node) &&
      accountIconRef.current &&
      !accountIconRef.current.contains(event.target as Node)
    ) {
      setAccountPop(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideNoti);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideNoti);
    };
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    // First try exact match
    let matchedTitle = pathToTitle[currentPath] || "";

    // If no exact match, try to match dynamic routes (with :id, :planId, etc.)
    if (!matchedTitle) {
      for (const [pathPattern, title] of Object.entries(pathToTitle)) {
        // Convert route pattern to regex (e.g., "/folder/:id" -> /^\/folder\/[^/]+$/)
        const patternRegex = new RegExp(
          "^" + pathPattern.replace(/:[^/]+/g, "[^/]+") + "$",
        );
        if (patternRegex.test(currentPath)) {
          matchedTitle = title;
          break;
        }
      }
    }

    setHeaderTitle(matchedTitle);

    const hideNavPaths = [
      "/dashboard",
      "/reset-password",
      "/tenant-management",
    ];

    if (hideNavPaths.includes(currentPath)) {
      setIsDashboard(true);
      setNav(false);
    } else {
      setNav(true);
      setIsDashboard(false);
    }
  }, [location, setNav]);

  useEffect(() => {
    if (user) {
      if (userType === "orgadmin") {
        // Try multiple sources for org admin name
        const orgAdminName =
          orgAdmin?.org_admin_name ||
          (user as any).org_admin_name ||
          (user as any).orgAdminName ||
          user.username;
        setUserDisplayName(orgAdminName || "Admin");
      } else if (userType === "orguser") {
        const firstName = (user as any).firstName || "";
        const lastName = (user as any).lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        setUserDisplayName(fullName || user.username || "User");
      } else {
        setUserDisplayName(user.username || "Admin");
      }
    } else {
      setUserDisplayName("");
    }
  }, [user, orgAdmin, orgUser, userType]);

  const handleProfileUpdate = (updatedData: {
    fullName: string;
    mobileNumber?: string;
    department?: string;
    organizationName?: string;
    adminTitle?: string;
  }) => {
    setUserDisplayName(updatedData.fullName);
  };

  return (
    <header>
      <div className="header">
        {(isNav || isDashboard) && (
          <div className={`logo logo-dark ${isDashboard ? "main-logo" : ""}`}>
            <Link to="/dashboard">
              <img src={images.appLogo} alt="Logo" />
            </Link>
          </div>
        )}

        <div className="nav-wrap">
          <ul className="nav-left">
            {!isDashboard && (
              <>
                <li className="desktop-toggle">
                  <a
                    className={`mr-1 cursor-pointer`}
                    onClick={() => setNav(!isNav)}
                  >
                    <i className="anticon"></i>
                  </a>
                </li>

                <li>
                  <h1>{headerTitle}</h1>
                </li>
              </>
            )}
          </ul>

          <ul className="nav-right">
            {!isDashboard && (
              <li className="dropdown dropdown-animated scale-left notifHeader mainDashboardIcon">
                <Link to="/dashboard" className="iconBgRounded">
                  <img className="anticon" src={maindashbaorIcom} alt="" />
                </Link>
              </li>
            )}

            {
              // isDashboard
              user?.roles.includes("orgadmin") && (
                <li className="dropdown  dropdown-animated scale-left notifHeader mainDashboardIcon">
                  <Link to="/regions" className="iconBgRounded">
                    <img
                      className="anticon"
                      src={settingsBtIcon}
                      alt="settings"
                    />
                  </Link>
                </li>
              )
            }

            <li className="dropdown dropdown-animated scale-left notifHeader">
              <a
                ref={notiIconRef}
                className="iconBgRounded cursor-pointer"
                onClick={() => {
                  setShowNotification(!showNotification);
                }}
              >
                <i className="anticon anticon-bell notification-badge"></i>
              </a>
              {showNotification && (
                <div ref={notificationRef}>
                  <Notification />
                </div>
              )}
            </li>

            <li
              ref={accountIconRef}
              className={`dropdown dropdown-animated userHeader scale-left`}
              onClick={() => setAccountPop(!accountPop)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setAccountPop(!accountPop);
                } else if (e.key === "Escape") {
                  setAccountPop(false);
                }
              }}
              tabIndex={0}
              role="button"
              aria-expanded={accountPop}
              aria-haspopup="menu"
            >
              <div className="pointer d-flex" data-toggle="dropdown">
                <div className="avatar avatar-image">
                  <i className="anticon anticon-user"></i>
                </div>
                <h5>{userDisplayName}</h5>
              </div>
              <div
                ref={accountRef}
                className={`dropdown-menu pop-profile ${
                  accountPop ? "show" : ""
                }`}
              >
                <a
                  className="dropdown-item d-block p-h-15 p-v-10 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setShowEditProfile(true);
                    setAccountPop(false);
                  }}
                  role="menuitem"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowEditProfile(true);
                      setAccountPop(false);
                    }
                  }}
                  aria-label="Edit Profile"
                >
                  <div className="d-flex align-items-center">
                    <i className="anticon anticon-edit opacity-04 font-size-16"></i>
                    <span className="m-l-10">Edit Profile</span>
                  </div>
                </a>
                <a
                  className="dropdown-item d-block p-h-15 p-v-10 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setShowResetPassword(true);
                    setAccountPop(false);
                  }}
                  role="menuitem"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowResetPassword(true);
                      setAccountPop(false);
                    }
                  }}
                  aria-label="Reset Password"
                >
                  <div className="d-flex align-items-center">
                    <i className="anticon anticon-lock opacity-04 font-size-16"></i>
                    <span className="m-l-10">Reset Password</span>
                  </div>
                </a>
                <div className="dropdown-divider"></div>
                <a
                  className="logoutHeader dropdown-item d-block p-h-15 p-v-10 cursor-pointer hover:bg-gray-50"
                  onClick={handleLogout}
                  role="menuitem"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleLogout();
                    }
                  }}
                  aria-label="Logout"
                >
                  <div className="d-flex align-items-center">
                    <button className="focus:outline-none" disabled={loading}>
                      <i className="anticon anticon-logout opacity-04 font-size-16"></i>
                      <span className="m-l-10">
                        {loading ? "Logging out..." : "Logout"}
                      </span>
                    </button>
                  </div>
                </a>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Profile Modals */}
      <EditProfile
        isOpen={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        orgUser={orgUser}
        user={user}
        orgAdmin={orgAdmin}
        onProfileUpdate={handleProfileUpdate}
      />
      <ResetPassword
        isOpen={showResetPassword}
        onClose={() => setShowResetPassword(false)}
      />
    </header>
  );
};

export default Header;
